"use client";

import { useEffect, useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";

import {
	AbsoluteCenter,
	Box,
	Button,
	Divider,
	FormControl,
	FormErrorMessage,
	Icon,
	IconButton,
	Input,
	InputGroup,
	InputLeftElement,
	InputRightElement,
	VStack,
	useBoolean,
	useToast,
} from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { At, Eye, EyeSlash, Lock } from "@phosphor-icons/react/dist/ssr";
import { FirebaseError } from "firebase/app";
import {
	AuthErrorCodes,
	getAuth,
	signInWithEmailAndPassword,
} from "firebase/auth";
import { useRouter } from "next/navigation";
import { z } from "zod";

import { errorFirstWrapAsync } from "@flowpos-workspace/global/utils/promise.utils";

import { AuthGoogleButton } from "@/app/(auth)/_components/auth-google-button";

import { useFirebaseStore } from "@/stores/firebase.store";

const signInFormSchema = z.object({
	email: z.string().email("Ingresa un correo electrónico válido."),
	password: z.string().min(1, "La contraseña es obligatoria."),
});

export const SignInForm = () => {
	const { getFirebaseApp } = useFirebaseStore((state) => ({
		getFirebaseApp: state.getFirebaseApp,
	}));
	const [auth, setAuth] = useState<ReturnType<typeof getAuth> | null>(null);
	const [showPassword, toggleShowPassword] = useBoolean(false);
	const [isAuthenticating, toggleIsAuthenticating] = useBoolean(false);
	const toast = useToast({ status: "error", duration: 3000, position: "top" });
	const { replace } = useRouter();

	const {
		handleSubmit,
		formState: { errors, isValid },
		register,
	} = useForm<z.infer<typeof signInFormSchema>>({
		mode: "onChange",
		resolver: zodResolver(signInFormSchema),
	});

	useEffect(() => {
		const initAuth = async () => {
			try {
				const firebaseApp = await getFirebaseApp();
				setAuth(getAuth(firebaseApp));
			} catch (error) {
				console.error("Failed to initialize Firebase auth:", error);
			}
		};

		initAuth();
	}, [getFirebaseApp]);

	const submitHandler: SubmitHandler<
		z.infer<typeof signInFormSchema>
	> = async ({ email, password }) => {
		if (!auth) {
			toast({
				description: "Authentication service not initialized yet.",
			});
			return;
		}

		toggleIsAuthenticating.on();
		const [error] = await errorFirstWrapAsync(
			signInWithEmailAndPassword(auth, email, password),
		);
		toggleIsAuthenticating.off();

		if (error) {
			if (
				error instanceof FirebaseError &&
				error.code === AuthErrorCodes.INVALID_LOGIN_CREDENTIALS
			) {
				return toast({
					description:
						"Parece que las credenciales que ingresaste no se encontraron. Por favor, regístrate con nosotros.",
				});
			}

			return toast({ description: "Unexpected error ocurred" });
		}

		replace("/");
	};

	if (!auth) {
		return null;
	}

	return (
		<VStack
			spacing={{ base: 6, lg: 10 }}
			as="form"
			onSubmit={handleSubmit(submitHandler)}
		>
			<VStack w="full" spacing={{ base: 4, lg: 8 }}>
				<FormControl isInvalid={!!errors.email} isDisabled={isAuthenticating}>
					<InputGroup>
						<InputLeftElement pointerEvents="none">
							<Icon as={At} fontSize="2xl" textColor="gray.400" />
						</InputLeftElement>
						<Input
							type="email"
							autoComplete="email"
							id="email"
							placeholder="Correo electrónico"
							{...register("email")}
						/>
					</InputGroup>
					<FormErrorMessage>{errors?.email?.message}</FormErrorMessage>
				</FormControl>
				<FormControl
					isInvalid={!!errors.password}
					isDisabled={isAuthenticating}
				>
					<InputGroup>
						<InputLeftElement pointerEvents="none">
							<Icon as={Lock} fontSize="2xl" textColor="gray.400" />
						</InputLeftElement>
						<Input
							type={showPassword ? "text" : "password"}
							id="password"
							placeholder="Contraseña"
							{...register("password")}
						/>
						<InputRightElement>
							<IconButton
								colorScheme="primary"
								variant="ghost"
								aria-label="show-hide-password"
								size="sm"
								rounded="full"
								onClick={toggleShowPassword.toggle}
								icon={<Icon as={showPassword ? EyeSlash : Eye} fontSize="xl" />}
							/>
						</InputRightElement>
					</InputGroup>
					<FormErrorMessage>{errors?.password?.message}</FormErrorMessage>
				</FormControl>
			</VStack>
			<Button
				w="full"
				isDisabled={!isValid}
				type="submit"
				isLoading={isAuthenticating}
			>
				Iniciar sesión
			</Button>
			<Box position="relative" w="full">
				<Divider borderColor="primary.100" />
				<AbsoluteCenter bg="white" px="4" textColor="primary.100">
					or
				</AbsoluteCenter>
			</Box>
			<AuthGoogleButton
				isAuthenticating={isAuthenticating}
				toggleIsAuthenticating={toggleIsAuthenticating}
				w="full"
				variant="outline"
			>
				Iniciar sesión con Google
			</AuthGoogleButton>
		</VStack>
	);
};
